export type Locale = 'de' | 'en';

// the outcome of loading a single locale catalog
export type CatalogLoadingResult =
  | { locale: Locale; status: 'success' }
  | { locale: Locale; status: 'failure'; error: unknown };

// defautl fallback locale
export const FALLBACK_LOCALE: Locale = 'de';

// localized message catalogs
const catalogs: Partial<Record<Locale, Record<string, string>>> = {};
const inflight: Partial<Record<Locale, Promise<CatalogLoadingResult>>> = {};

const catalogUrl = (locale: Locale) => `/assets/error-codes-${locale}.json`;

/**
 * Fetch a locale catalog into the cache (idempotent and deduplicated)
 *
 * Never rejects: on failure it logs and resolves with a failure result so
 * callers can report it; reporting (e.g. Sentry) is intentionally left to them.
 */
export const loadErrorCatalog = (
  locale: Locale
): Promise<CatalogLoadingResult> => {
  if (catalogs[locale]) return Promise.resolve({ locale, status: 'success' });
  const existing = inflight[locale];
  if (existing) return existing;

  const promise = fetch(catalogUrl(locale))
    .then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json() as Promise<Record<string, string>>;
    })
    .then((data): CatalogLoadingResult => {
      catalogs[locale] = data;
      return { locale, status: 'success' };
    })
    .catch((error: unknown): CatalogLoadingResult => {
      console.warn(`[errors] Failed to load catalog "${locale}":`, error);
      return { locale, status: 'failure', error };
    })
    .finally(() => {
      delete inflight[locale];
    });

  inflight[locale] = promise;
  return promise;
};

/**
 * Preload the active locale (and the fallback)
 *
 * Call once on startup. Resolves with one result per catalog attempted, each
 * indicating success or failure (callers decide how to handle failures).
 */
export const loadErrorCatalogs = async (): Promise<CatalogLoadingResult[]> => {
  const locale = resolveLocale();
  const locales: Locale[] =
    locale === FALLBACK_LOCALE ? [locale] : [locale, FALLBACK_LOCALE];

  return Promise.all(locales.map(loadErrorCatalog));
};

/**
 * Resolve the active locale from the browser
 */
export const resolveLocale = (): Locale => {
  const language = (typeof navigator !== 'undefined' ? navigator.language : '')
    .toLowerCase()
    .split('-')[0];

  return language === 'en' ? 'en' : FALLBACK_LOCALE;
};

/**
 * Look up a localized template by key
 *
 * Prefer the active locale and fall back to the German catalog
 * (undefined until catalogs are loaded).
 */
export const lookupTemplate = (
  key: string,
  locale: Locale
): string | undefined =>
  catalogs[locale]?.[key] ?? catalogs[FALLBACK_LOCALE]?.[key];

/**
 * Substitute {name} placeholders with runtime values
 *
 * Keep the placeholder literal when an argument is missing
 * (matches the backend's render contract).
 */
export const substitute = (
  template: string,
  args: Record<string, string> = {}
) => template.replace(/\{(\w+)\}/g, (_, name) => args[name] ?? `{${name}}`);
